import React from "react";
import "./style.scss";
import {
  addDays,
  format,
  isSameDay,
  startOfDay,
  differenceInCalendarDays,
} from "date-fns";
import { pl } from "date-fns/locale";

interface IProps {
  onMobileDateChange: (date: Date) => void;
  daysAhead?: number;   // ile dni do przodu generujemy (domyślnie 60)
  pageSize?: number;    // krok strzałek (domyślnie 7)
  dateFromState?: Date | string | null; // zewnętrzny wybór dnia
}

const DOW_ABBR = ["ND", "PN", "WT", "ŚR", "CZ", "PT", "SB"]; // JS: 0=ND

const MobileExerciseChoice: React.FC<IProps> = ({
  onMobileDateChange,
  daysAhead = 60,
  pageSize = 3,
  dateFromState = null,
}) => {
  const today = startOfDay(new Date());
  const [selectedDate, setSelectedDate] = React.useState<Date>(today);

  // wygenerowane dni: dziś + N do przodu
  const days = React.useMemo(
    () => Array.from({ length: daysAhead + 1 }, (_, i) => startOfDay(addDays(today, i))),
    [today, daysAhead]
  );

  // refs do scrolla i chipów
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const chipRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());
  const setChipRef = (date: Date) => (el: HTMLButtonElement | null) => {
    const key = date.toISOString();
    if (!el) chipRefs.current.delete(key);
    else chipRefs.current.set(key, el);
  };

  const scrollIntoViewIfNeeded = (date: Date) => {
    const key = date.toISOString();
    const chip = chipRefs.current.get(key);
    const scroller = scrollRef.current;
    if (!chip || !scroller) return;
    const chipRect = chip.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const isVisible = chipRect.left >= scRect.left && chipRect.right <= scRect.right;
    if (!isVisible) {
      chip.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  };

  const handleSelect = (date: Date) => {
    setSelectedDate(date);
    onMobileDateChange(date);
    scrollIntoViewIfNeeded(date);
  };

  const pageScroll = (dir: -1 | 1) => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const chipWidth = 74; // przybliżona szerokość „chipa”
    scroller.scrollBy({ left: dir * pageSize * chipWidth, behavior: "smooth" });
  };

  // 🔹 Reakcja na zewnętrzny props dateFromState
  React.useEffect(() => {
    if (!dateFromState) return;

    const parsed =
      dateFromState instanceof Date ? dateFromState : new Date(dateFromState);
    if (isNaN(parsed.getTime())) return; // niepoprawna data — ignorujemy

    const next = startOfDay(parsed);

    // Clamp do zakresu (dzis .. ostatni dzień listy), żeby nie wyjść poza wygenerowane dni
    const last = days[days.length - 1];
    const clamped =
      next < today ? today : next > last ? last : next;

    setSelectedDate(clamped);
    // UWAGA: celowo nie wywołujemy onMobileDateChange, to „controlled from parent”
    // Jeśli chcesz, odkomentuj:
    // onMobileDateChange(clamped);

    // przewiń, żeby było widać
    // (dla DZIŚ przewijamy na hard 0 – UX „reset”)
    if (isSameDay(clamped, today)) {
      scrollRef.current?.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      scrollIntoViewIfNeeded(clamped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFromState, days.length]); // reaguj na zmianę propsa lub długości listy

  // klawiatura ← →
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      const delta = e.key === "ArrowLeft" ? -1 : 1;
      const idx = differenceInCalendarDays(selectedDate, today);
      const nextIdx = Math.max(0, Math.min(days.length - 1, idx + delta));
      handleSelect(days[nextIdx]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, days]);

  const isTodaySelected = isSameDay(selectedDate, today);

  return (
    <div className="mexc">
      <button
        className="mexc__nav mexc__nav--left"
        aria-label={`Wstecz o ${pageSize} dni`}
        onClick={() => pageScroll(-1)}
      >
        <span aria-hidden>‹</span>
      </button>

      {/* Sticky DZIŚ */}
      <div className="mexc__today">
        <button
          className={["mexc__chip", isTodaySelected ? "is-active" : "is-today"].join(" ")}
          onClick={() => {
            handleSelect(today);
            scrollRef.current?.scrollTo({ left: 0, behavior: "smooth" });
          }}
          role="tab"
          aria-selected={isTodaySelected}
          aria-label={`${format(today, "EEEE", { locale: pl })}, ${format(today, "d.MM", { locale: pl })} (Dziś)`}
        >
          <span className="mexc__dow">{DOW_ABBR[today.getDay()]}</span>
          <span className="mexc__date">DZIŚ</span>
        </button>
      </div>

      {/* Scroller od jutra */}
      <div
        className="mexc__scroller"
        role="tablist"
        aria-label="Wybierz dzień"
        ref={scrollRef}
      >
        {days.map((date, i) => {
          if (i === 0) return null; // DZIŚ już mamy po lewej
          const active = isSameDay(selectedDate, date);
          const labelDate = i === 1 ? "JUTRO" : format(date, "d.MM", { locale: pl });
          const dow = DOW_ABBR[date.getDay()];
          const aria = `${format(date, "EEEE", { locale: pl })}, ${format(date, "d.MM", { locale: pl })}`;

          return (
            <button
              key={date.toISOString()}
              ref={setChipRef(date)}
              role="tab"
              aria-selected={active}
              aria-label={aria}
              className={["mexc__chip", active ? "is-active" : ""].join(" ")}
              onClick={() => handleSelect(date)}
            >
              <span className="mexc__dow">{dow}</span>
              <span className="mexc__date">{labelDate}</span>
            </button>
          );
        })}
      </div>

      <button
        className="mexc__nav mexc__nav--right"
        aria-label={`Dalej o ${pageSize} dni`}
        onClick={() => pageScroll(1)}
      >
        <span aria-hidden>›</span>
      </button>

      <div className="mexc__edge mexc__edge--left" aria-hidden="true" />
      <div className="mexc__edge mexc__edge--right" aria-hidden="true" />
    </div>
  );
};

export default MobileExerciseChoice;
